<template>
    <div>
        <skeleton-loader v-if="loading" type="lib-rows" :count="perPage" />

        <div v-else-if="routines.length === 0" class="lib-empty">
            <small>{{ $t('routines.no_routines_founded') }}</small>
            <div class="mt-3">
                <v-btn @click="createNew" color="primary" class="black--text" depressed>
                    <v-icon left>mdi-plus</v-icon>
                    {{ $t('routines.create_first_routine') }}
                </v-btn>
            </div>
        </div>

        <div v-else class="lib-list">
            <routine-card v-for="routine in routines" :key="routine.objectID" :routine="routine" :no-folder="noFolder"
                with-menu @click:delete="showDeleteRoutine(routine.objectID)" @click:folder="showSaveFolder(routine)"
                @click:assign="showAssignRoutineDialog(routine)" no-duration-text @click:edit="showEditRoutine(routine)"
                @click:duplicated="onDuplicatedRoutine"
                @mousedown="startDrag" @click:share="showShareRoutineDialog(routine)" :canShare="canShare" />
        </div>

        <kimsa-pagination class="mt-4" @page="onPageChange" :value="page + 1" :pageCount="pageCount" center
            :total-visible="isMobile ? 4 : (totalVisiblePagination || 7)" />

        <save-in-folder-dialog ref="saveInFolder" :current-id="routineId" :default-folder="folderId"
            @cancel="routineId = null; folderId = null" type="routines" />

        <assign-routine-dialog ref="assign" @hide="routineId = null" :routine-id="routineId" :coach-id="coachId" />

        <routine-editor ref="routineEditor" :coach-id="coachId" @success="onRoutineSaved" />

        <routine-card v-if="dragging" :routine="draggingItem" with-menu no-duration-text class="floating-drag-item" />

        <delete-dragging-button :dragging="dragMoved" @mouseup="showDeleteRoutine(draggingItem.objectID)" />

        <kimsa-confirm ref="showDelete" :title="$t('routines.delete_routine')"
            :content="$t('routines.confirm_deleted_routine')" :persistent="false" :loading="loadingDelete"
            @confirm="deleteRoutine" @cancel="routineId = null" :close-on-confirm="false" :alert-mode="true" />

        <share-routine-dialog ref="share" />

    </div>
</template>

<script>

import RoutineCard from '@/components/common/routines/RoutineCard'
import { RoutineIndex } from "@/utils/Algolia";
import { mapActions } from "vuex";
import { db, Timestamp } from "@/utils/Firebase";
import KimsaConfirm from "@/components/common/KimsaConfirm";
import { debounce } from "@/utils/Common";
import SkeletonLoader from "@/components/common/SkeletonLoader";
import SaveInFolderDialog from "@/components/coach/SaveInFolderDialog";
import KimsaPagination from "@/components/common/KimsaPagination";
import AssignRoutineDialog from './AssignRoutineDialog.vue';
import RoutineEditor from './RoutineEditor.vue';
import DeleteDraggingButton from '../../common/DeleteDraggingButton.vue';
import draggingMixin from '../../../mixins/draggingMixin';
import ShareRoutineDialog from './ShareRoutineDialog.vue';
import algoliaInlineItemMixin from '../../../mixins/algoliaInlineItemMixin';

export default {
    mixins: [draggingMixin, algoliaInlineItemMixin],
    components: {
        KimsaPagination,
        SaveInFolderDialog,
        SkeletonLoader,
        KimsaConfirm,
        RoutineCard,
        AssignRoutineDialog,
        DeleteDraggingButton,
        RoutineEditor,
        ShareRoutineDialog,
    },
    props: {
        search: {
            type: String,
            default: '',
        },
        folder: {
            type: String,
            default: '',
        },
        noFolder: {
            type: Boolean,
            default: false,
        },
        totalVisiblePagination: {
            type: [Number, String],
            default: null,
        },
        coachId: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            loading: false,
            routines: [],
            routineId: null,
            loadingDelete: false,
            coaches: [],
            folderId: null,
            page: 0,
            pageCount: 0,
            perPage: 6,
        }
    },
    computed: {
        companyId(vm = this) {
            return vm.account?.coachSubscription?.companyId || vm.currentUser.uid
        },
        canShare() {
            return this.isCompany || this.account?.accountType === 'coach_company'
        },
    },
    async mounted() {
        let vm = this
        vm.loading = true
        vm.isCompany ? await vm.loadCoaches() : await vm.loadRoutines()
    },
    methods: {
        ...mapActions(['fetchCompanyCoaches']),
        async loadCoaches() {
            const vm = this
            const coaches = await vm.fetchCompanyCoaches(vm.companyId)
            vm.coaches = [vm.coachId, ...coaches.map(c => c.objectID)]
        },
        createNew() {
            const vm = this
            vm.$refs.routineEditor.showCreate()
        },
        goToRoutine(id) {
            const vm = this
            vm.pushRoute({ name: `${vm.accountType}.routines.detail`, params: { uid: id } })
        },
        async showDeleteRoutine(id) {
            const vm = this
            vm.resetDrag()
            vm.routineId = id
            vm.$refs.showDelete.show()
        },
        async deleteRoutine() {
            const vm = this
            await vm.withLoading(async () => {
                await db.collection('routines').doc(vm.routineId).delete()
                vm.routines = vm.markDeletedInline(vm.routines, vm.routineId)
                vm.toast(vm.$t('routines.deleted'))
                vm.routineId = null
                vm.$refs.showDelete?.hide()
            }, 'loadingDelete')
        },
        debouncedLoadRoutines: debounce(
            function () {
                this.loadRoutines()
            }, 1000),
        async loadRoutines() {
            const vm = this
            await vm.withLoading(async () => {
                vm.routines = []
                let coaches = vm.coaches.length === 0 ? vm.coachId : vm.coaches.join(' OR coachId:')
                let filter = `coachId: ${coaches}`
                if (vm.folder) filter += ` AND folder: ${vm.folder}`
                let response = await RoutineIndex.search(vm.search, {
                    filters: filter,
                    hitsPerPage: vm.perPage,
                    page: vm.page,
                })
                vm.pageCount = response.nbPages || 0
                vm.routines = vm.filterDeletedInline(response.hits)
            })
        },
        showSaveFolder(routine) {
            const vm = this
            vm.routineId = routine.objectID
            vm.folderId = routine.folder || null
            vm.$refs.saveInFolder.show()
        },
        refresh() {
            const vm = this;
            vm.loadRoutines()
        },
        onRoutineSaved(routine) {
            const vm = this
            if (!vm.addItemInline(vm.routines, routine, 'folder', vm.folder)) vm.loadRoutines()
        },
        onPageChange(p) {
            const vm = this
            const newPage = Number(p) - 1
            if (newPage === vm.page) return
            vm.page = newPage
            vm.loadRoutines()
        },
        showAssignRoutineDialog(routine) {
            const vm = this;
            vm.routineId = routine.objectID
            vm.$refs.assign.show()
        },
        showEditRoutine(routine) {
            const vm = this;
            vm.$refs.routineEditor.showEdit(routine)
        },
        async onDuplicatedRoutine(routine) {
            const vm = this
            vm.$refs.routineEditor.showEdit(routine)
            await vm.loadRoutines()
        },
        async handleDoubleClickDragging(item) {
            const vm = this;
            let routine = { ...item }

            if (!routine.blocks) {
                let ref = await db.collection('routines').doc(routine.objectID).get()
                if (!ref.exists) return vm.toast(vm.$t('common.an_error_has_occurred'), 'error')
                routine = ref.data()
            }

            routine.owner = 'user'
            routine.coachId = vm.coachId
            routine.name = `${routine.name} ${vm.$t('common.copy_template')}`
            delete routine.created_at
            delete routine.objectID

            routine = JSON.parse(JSON.stringify(routine))

            let newRoutine = await db.collection('routines').add({ ...routine, created_at: Timestamp.now() })

            if (!vm.$refs.routineEditor) return
            vm.$refs.routineEditor.showEdit({ objectID: newRoutine.id })
        },
        handleClickDragging(item) {
            const vm = this;
            vm.goToRoutine(item.objectID)
        },
        showShareRoutineDialog(routine) {
            const vm = this;
            vm.$refs.share.show(routine)
        }
    },
    watch: {
        coaches() {
            this.loadRoutines()
        },
        search(val) {
            this.page = 0
            if (!val) return this.loadRoutines()
            return this.debouncedLoadRoutines()
        },
        folder() {
            this.page = 0
            this.loadRoutines()
        },
        loading(val) {
            this.$emit('loading', val)
        },
    }
}
</script>

<style scoped>
.lib-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.lib-empty {
    text-align: center;
    padding: 32px 0;
    color: var(--v-txtMuted-base);
}

.floating-drag-item {
    position: fixed;
    z-index: 9999;
    opacity: var(--drag-opacity);
    width: 250px;
    left: 0;
    top: 0;
    transform: translate(var(--drag-x), var(--drag-y));
    pointer-events: none;
}
</style>
